import { StudentEntrySource, StudentTypes } from "../../schemas"
import { StudentEnrollmentTypeEnum } from "../../constants"
import z from "zod"

export const ProjectTypeEnum = z.enum(
  ["inburgering", "web", "taal_in_bedrijf", "tof"],
  { message: "Contract type is verplicht" },
)

export const ProjectTargetGroupEnum = z.enum(
  [
    "web_non_formeel",
    "web_nt1",
    "web_nt2",
    "web_nt1_nt2",

    "wi2013_elip",
    "wi2013_civic",
    "wi2021_family",
    "wi2021_status",
  ],
  { message: "Doelgroep is verplicht" },
)

export const projectTypeOptions: {
  value: ProjectType
  label: string
}[] = [
  { value: "inburgering", label: "Inburgering" },
  { value: "web", label: "WEB" },
  { value: "taal_in_bedrijf", label: "Taal in Bedrijf" },
  { value: "tof", label: "TOF" },
]

export const projectTargetGroupOptions: {
  value: ProjectTargetGroup
  label: string
}[] = [
  { value: "web_non_formeel", label: "WEB Algemeen" },
  { value: "web_nt1", label: "WEB NT1" },
  { value: "web_nt2", label: "WEB NT2" },
  { value: "web_nt1_nt2", label: "WEB NT1/NT2" },

  { value: "wi2013_elip", label: "WI 2013 ELIP" },
  { value: "wi2013_civic", label: "WI 2013 Inburgeraar" },
  { value: "wi2021_family", label: "WI 2021 Gezinsmigranten" },
  { value: "wi2021_status", label: "WI 2021 Statushouder" },
]

export const projectTypeMap: Record<ProjectType, string> = {
  inburgering: "Inburgering",
  web: "WEB",
  taal_in_bedrijf: "Taal in Bedrijf",
  tof: "TOF",
}

export const projectTargetGroupMap: Record<ProjectTargetGroup, string> = {
  web_non_formeel: "WEB algemeen",
  web_nt1: "WEB NT1",
  web_nt2: "WEB NT2",
  web_nt1_nt2: "WEB NT1/NT2",

  wi2013_elip: "WI 2013 ELIP",
  wi2013_civic: "WI 2013 Inburgeraar",
  wi2021_family: "WI 2021 Gezinsmigranten",
  wi2021_status: "WI 2021 Statushouder",
}

export type ProjectType = z.infer<typeof ProjectTypeEnum>

export type ProjectTargetGroup = z.infer<typeof ProjectTargetGroupEnum>

export const projectRoutesEnum = z.enum(
  ["b1_route", "b1_route_entree", "z_route", "nvt"],
  {
    message: "Contract route is verplicht",
  },
)

export type ProjectRoute = z.infer<typeof projectRoutesEnum>

export const projectRouteOptions: {
  value: ProjectRoute
  label: string
}[] = [
  { value: "b1_route", label: "B1 Route" },
  { value: "z_route", label: "Z-Route" },
  { value: "b1_route_entree", label: "B1 Route Entree" },
  { value: "nvt", label: "N.V.T." },
]

export const projectRoutesMap: Record<ProjectRoute, string> = {
  b1_route: "B1 Route",
  z_route: "Z-Route",
  b1_route_entree: "B1 Route Entree",
  nvt: "N.V.T.",
}

export function formatProjectRoutes(
  routes: ProjectRoute[] | null | undefined,
): string | null {
  if (!routes?.length) return null

  return routes.map((r) => projectRoutesMap[r]).join(", ")
}

type RouteCapacityConfig = {
  route: ProjectRoute
  label: string
  minField: "minCapacityZRoute" | "minCapacityB1Route"
  maxField: "maxCapacityZRoute" | "maxCapacityB1Route"
}

export const projectRouteCapacityConfig: RouteCapacityConfig[] = [
  {
    route: "z_route",
    label: "Z-route",
    minField: "minCapacityZRoute",
    maxField: "maxCapacityZRoute",
  },
  {
    route: "b1_route",
    label: "B1-route",
    minField: "minCapacityB1Route",
    maxField: "maxCapacityB1Route",
  },
]

export const subcontractorEnum = z.enum(
  ["sosytraining", "meerwaarde", "stimulance"],
  {
    message: "Onderaannemer optie is verplicht",
  },
)

export type Subcontractor = z.infer<typeof subcontractorEnum>

export const subcontractorOptions: { value: Subcontractor; label: string }[] = [
  { value: "sosytraining", label: "Sosytraining" },
  { value: "meerwaarde", label: "Meerwaarde" },
  { value: "stimulance", label: "Stimulance" },
]

export const subcontractorMap: Record<Subcontractor, string> = {
  sosytraining: "Sosytraining",
  meerwaarde: "Meerwaarde",
  stimulance: "Stimulance",
}

export const subsidyRegulationEnum = z.enum(
  [
    "geen",
    "ras",
    "slim",
    "oom",
    "soob",
    "sol",
    "doorzaam",
    "esf_plus",
    "ofp",
    "sfcbv",
  ],
  {
    message: "Subsidie regeling is verplicht",
  },
)

export type SubsidyRegulation = z.infer<typeof subsidyRegulationEnum>

export const subsidyRegulationOptions: {
  value: SubsidyRegulation
  label: string
}[] = [
  { value: "geen", label: "Geen" },
  { value: "ras", label: "RAS" },
  { value: "slim", label: "SLIM" },
  { value: "oom", label: "OOM" },
  { value: "soob", label: "SOOB" },
  { value: "sol", label: "SOL" },
  { value: "doorzaam", label: "Doorzaam" },
  { value: "esf_plus", label: "ESF Plus" },
  { value: "ofp", label: "OFP" },
  { value: "sfcbv", label: "SFCBV" },
]

export const subsidyRegulationMap: Record<SubsidyRegulation, string> = {
  geen: "Geen",
  ras: "RAS",
  slim: "SLIM",
  oom: "OOM",
  soob: "SOOB",
  sol: "SOL",
  doorzaam: "Doorzaam",
  esf_plus: "ESF Plus",
  ofp: "OFP",
  sfcbv: "SFCBV",
}

export const lessonDeadlineTriggerEnum = z.enum(["aanmelding", "intake"], {
  message: "Les deadline trigger is verplicht",
})

export type LessonDeadlineTrigger = z.infer<typeof lessonDeadlineTriggerEnum>

export const lessonDeadlineTriggerOptions: {
  value: LessonDeadlineTrigger
  label: string
}[] = [
  { value: "aanmelding", label: "Aanmelding" },
  { value: "intake", label: "Intake" },
]

export const inburgeringTargetGroups: ProjectTargetGroup[] = [
  "wi2013_civic",
  "wi2013_elip",
  "wi2021_family",
  "wi2021_status",
]

export const webTargetGroups: ProjectTargetGroup[] = [
  "web_non_formeel",
  "web_nt1",
  "web_nt2",
  "web_nt1_nt2",
]

export const getTargetGroupOptionsForType = (type?: ProjectType | null) => {
  if (type === "inburgering") {
    return projectTargetGroupOptions.filter((o) =>
      inburgeringTargetGroups.includes(o.value),
    )
  }
  if (type === "web") {
    return projectTargetGroupOptions.filter((o) =>
      webTargetGroups.includes(o.value),
    )
  }

  return []
}
export interface StudentProjectOrganizationDetails {
  id: number
  project: {
    id: number
    name: string
    type: string
    isEntrySourceRequired: boolean | null
  }
  organization: {
    id: number
    name: string
  }
  billingOrganization: {
    id: number
    name: string
  }
  reportingOrganization: {
    id: number
    name: string
  }
  studentType: StudentTypes | null
  externalStudentFileNumber: string | null
  entrySource: StudentEntrySource | null
  rejectedMessage: string | null
  enrollmentType: StudentEnrollmentTypeEnum
}
